/* eslint-disable no-nested-ternary */
/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import LinkContainer from '@components/Cards/ListInfoCard/LinkContainer';
import ListInfoCard from '@components/Cards/ListInfoCard';
import NotAddedAccount from '@components/NotAddedAccount';
import * as routes from '@constants/routes';
import { useBankAccount, useAuth } from '@hooks';
import { getEnv } from '@config';
import { extractCciFromBankAccount } from '@helpers/bankAccount';
import { bankAccountType } from '@constants/bankAccountType';
import { capitalize } from '@helpers';
import { isCountryPeru, isCountryColombia } from 'constants/regions';

const CommerceData = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { bankAccount, deleteBankAccount } = useBankAccount();
  const [sections, setSections] = useState([]);
  const { merchant, isAllowed, isSeller, isLegalAgent, isLegalEntity } = useAuth();
  const allowed = isAllowed('USERS');
  const { country } = getEnv();
  const showOneDayCompany = ['cl', 'co'].includes(country);

  useEffect(() => {
    const commerceSection = {
      title: t('commerceData.commerce.title'),
      data: [
        {
          label: t('commerceData.commerce.nameFantasy'),
          value: capitalize(isLegalEntity() ? merchant.merchant_name : merchant.business_name),
        },
        { label: t('commerceData.commerce.rut'), value: merchant.document?.doc_number },
        { label: t('commerceData.commerce.address'), value: merchant.address?.formatted_address },
        { label: t('commerceData.commerce.addressInfo'), value: capitalize(merchant.address_info) },
        { label: t('commerceData.commerce.zipCode'), value: merchant.address?.zip_code },
        {
          label: t('commerceData.commerce.legalEntity'),
          value: capitalize(merchant.business_name),
        },
        { label: t('commerceData.commerce.activity'), value: merchant.activity?.category },
      ],
      showEdit: isAllowed('MERCHANT_UPDATE') && !isSeller() && isLegalAgent(),
      handleEdit: () => {
        history.push(routes.EDIT_COMMERCE);
      },
    };

    const { first_name, patern_last, matern_last, document, email, phones } =
      merchant.legal_agent ?? {};

    const { country_code, region_id, number: phoneNumber } = phones ? phones[0] : {};

    const legalSection = {
      title: t('commerceData.legal.title'),
      data: [
        { label: t('commerceData.legal.name'), value: capitalize(first_name) },
        { label: t('commerceData.legal.last_name'), value: capitalize(patern_last) },
        { label: t('commerceData.legal.matern_last_name'), value: capitalize(matern_last) },
        { label: t('common.email'), value: email },
        { label: t('common.phone'), value: `${country_code} ${region_id}${phoneNumber}` },
      ],
      showEdit: isAllowed('MERCHANT_UPDATE') && !isSeller() && isLegalAgent(),
      handleEdit: () => {
        history.push(routes.EDIT_REPRESENTANT);
      },
    };

    if (isLegalEntity()) {
      const wordingDocTypeByCountry = {
        cl: 'commerceData.legal.doc_number',
        pe: `commerceData.legal.docType.${document?.doc_type.toLowerCase()}`,
        co: `commerceData.legal.docType.${document?.doc_type.toLowerCase()}`,
      };
      legalSection.data.splice(3, 0, {
        label: t(wordingDocTypeByCountry[country]),
        value: document?.doc_number,
      });
    }
    if (showOneDayCompany) {
      commerceSection.data.push(
        ...[
          {
            label: t('commerceData.commerce.company'),
            value: merchant.one_day_company ? 'Si' : 'No',
          },
        ]
      );
    }

    const bankSection = {
      title: t('commerceData.bankAccount.title'),
      data: bankAccount
        ? [
            {
              label: t('commerceData.bankAccount.document'),
              value: `${
                isCountryPeru
                  ? `${bankAccount.owner_data.doc_type} - `
                  : isCountryColombia
                  ? `${t(
                      `commerceData.legal.docType.${bankAccount?.owner_data?.doc_type?.toLowerCase()}`
                    )} - `
                  : ''
              }${bankAccount.owner_data.doc_number}`,
            },
            {
              label: t('commerceData.bankAccount.bank'),
              value: capitalize(bankAccount.bank_data?.finantial_institution_name),
            },
            {
              label: t('commerceData.bankAccount.accountNumber'),
              value: bankAccount.account_data.account_number,
            },
            {
              label: t('commerceData.bankAccount.accountType'),
              value: bankAccountType[bankAccount.account_data.account_type_code],
            },
          ]
        : [],
      emptyContent: (
        <NotAddedAccount
          hideDescription={false}
          noPadding
          handleAddAccount={() => {
            history.push(routes.ADD_BANK_ACCOUNT);
          }}
        />
      ),
      showEdit: isAllowed('WITHDRAW_MODIFY_BANK_ACCOUNT') && !!bankAccount,
      handleEdit: () => {
        history.push(routes.EDIT_BANK_ACCOUNT);
      },
      showDelete: isAllowed('WITHDRAW_MODIFY_BANK_ACCOUNT') && !!bankAccount,
      handleDelete: () => {
        deleteBankAccount();
      },
    };
    if (bankAccount && country === 'pe')
      bankSection.data.splice(1, 0, {
        label: t('commerceData.bankAccount.cci'),
        value: extractCciFromBankAccount(bankAccount),
      });

    const newSections = [commerceSection, legalSection];
    if (isAllowed('WITHDRAW_CREATE_BANK_ACCOUNT')) {
      newSections.push(bankSection);
    }
    setSections(newSections);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchant, bankAccount]);

  return (
    <>
      {allowed && <LinkContainer />}
      {sections.map(
        ({ title, data, emptyContent, showEdit, handleEdit, showDelete, handleDelete }, index) => (
          <ListInfoCard
            key={index}
            title={title}
            data={data}
            showEdit={showEdit}
            handleEdit={handleEdit}
            showDelete={showDelete}
            handleDelete={handleDelete}
            emptyContent={emptyContent}
          />
        )
      )}
    </>
  );
};

export default CommerceData;
